import React, {useEffect, useState} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {ordersService} from '../../services/ordersService';
import {AdminOrderFilter, AdminOrderResponse, OrdersRequest, OrderStatus} from '../../types/orders';
// import CreateOrderModal from '../modals/CreateOrderModal';
import './Page.css';

const PAGE_AUTO_REFRESH_SECONDS = 30;

const Orders: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [orders, setOrders] = useState<AdminOrderResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pageSize] = useState(20);
  const [secondsUntilRefresh, setSecondsUntilRefresh] = useState(PAGE_AUTO_REFRESH_SECONDS);
  // const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  // const [successMessage, setSuccessMessage] = useState('');

  // Read from URL params or use defaults
  const searchQuery = searchParams.get('search') || '';
  const filter = (searchParams.get('filter') as AdminOrderFilter) || AdminOrderFilter.GROUP_ACTIVE_ORDERS;
  const currentPage = parseInt(searchParams.get('page') || '1', 10);

  // Helper functions to update URL params
  const updateSearchQuery = (value: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set('search', value);
    } else {
      newParams.delete('search');
    }
    newParams.set('page', '1'); // Reset to page 1 on search
    setSearchParams(newParams);
  };

  const updateFilter = (value: AdminOrderFilter) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('filter', value);
    newParams.set('page', '1'); // Reset to page 1 on filter change
    setSearchParams(newParams);
  };

  const updatePage = (page: number) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('page', page.toString());
    setSearchParams(newParams);
  };

  const fetchOrders = async () => {
    setLoading(true);
    setError('');

    try {
      const request: OrdersRequest = {
        searchQuery,
        filter,
        page: currentPage - 1, // Backend uses 0-based indexing
        pageSize
      };

      const response = await ordersService.getOrders(request);

      if (response.success && response.data) {
        setOrders(response.data);
      } else {
        setError(response.error || 'Failed to fetch orders');
        setOrders([]);
      }
    } catch (error) {
      setError('Network error. Please try again.');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const handleManualRefresh = () => {
    fetchOrders();
    setSecondsUntilRefresh(PAGE_AUTO_REFRESH_SECONDS);
  };

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, filter, currentPage]);

  // Single timer that runs every second
  useEffect(() => {
    const intervalId = setInterval(() => {
      setSecondsUntilRefresh((prev) => {
        if (prev <= 1) {
          fetchOrders();
          return PAGE_AUTO_REFRESH_SECONDS;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, filter, currentPage]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // The fetchOrders will be triggered by useEffect when URL params change
  };

  // const handleCreateOrder = (orderId: number) => {
  //   setIsCreateModalOpen(false);
  //   setSuccessMessage(t('orders.orderCreated', { orderId }));
  //   // Clear success message after 3 seconds
  //   setTimeout(() => setSuccessMessage(''), 3000);
  //   // Navigate to the new order details
  //   navigate(`/order-details/${orderId}`);
  // };

  const getStatusDisplay = (order: AdminOrderResponse): string => {
    if (order.restaurantRejectionReason && order.orderStatus == OrderStatus.ORDER_ACCEPTED_BY_ADMIN) {
        return t('orders.status.restaurantRejected');
    }

    if (order.problematicCompleteReason && order.orderStatus == OrderStatus.ORDER_COMPLETED) {
        return t('orders.status.completedWithProblem');
    }

    const status = order.orderStatus;
    const statusMap: Record<OrderStatus, string> = {
      [OrderStatus.ORDER_PLACED]: t('orders.status.orderPlaced'),
      [OrderStatus.ORDER_ACCEPTED_BY_ADMIN]: t('orders.status.acceptedByAdmin'),
      [OrderStatus.RESTAURANT_ACCEPTED_ORDER]: t('orders.status.restaurantAccepted'),
      [OrderStatus.RESTAURANT_FINISHED_ORDER]: t('orders.status.readyForPickup'),
      [OrderStatus.DRIVER_PICKED_UP_ORDER]: t('orders.status.inDelivery'),
      [OrderStatus.DRIVER_DROPPED_OFF_ORDER]: t('orders.status.delivered'),
      [OrderStatus.ORDER_COMPLETED]: t('orders.status.completed'),
      [OrderStatus.ADMIN_CANCELLED_ORDER]: t('orders.status.cancelled'),
    };
    return statusMap[status] || status;
  };

  const getStatusColor = (order: AdminOrderResponse): string => {
    if (order.restaurantRejectionReason && order.orderStatus == OrderStatus.ORDER_ACCEPTED_BY_ADMIN) {
        return '#dc3545';
    }

    const status = order.orderStatus;
    switch (status) {
      case OrderStatus.ORDER_PLACED:
        return '#000000';
      case OrderStatus.ORDER_ACCEPTED_BY_ADMIN:
        return '#17a2b8';
      case OrderStatus.RESTAURANT_ACCEPTED_ORDER:
        return '#fd7e14';
      case OrderStatus.RESTAURANT_FINISHED_ORDER:
        return '#20c997';
      case OrderStatus.DRIVER_PICKED_UP_ORDER:
        return '#007bff';
      case OrderStatus.DRIVER_DROPPED_OFF_ORDER:
        return '#6f42c1';
      case OrderStatus.ORDER_COMPLETED:
        return '#28a745';
      case OrderStatus.ADMIN_CANCELLED_ORDER:
        return '#dc3545';
      default:
        return '#000000';
    }
  };

  const formatPrice = (price: number): string => {
    return `${price.toLocaleString()} soum`;
  };

  const formatDate = (timestamp: number): string => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleOrderClick = (orderId: number) => {
    navigate(`/order-details/${orderId}`);
  };

  const filterOptions = [
    { value: AdminOrderFilter.GROUP_ACTIVE_ORDERS, label: t('orders.filters.active') },
    { value: AdminOrderFilter.PENDING, label: t('orders.filters.pending') },
    { value: AdminOrderFilter.RESTAURANT_NOT_ACCEPTED, label: t('orders.filters.restaurantNotAccepted') },
    { value: AdminOrderFilter.RESTAURANT_COOKING, label: t('orders.filters.restaurantCooking') },
    { value: AdminOrderFilter.WITHOUT_DRIVER, label: t('orders.filters.withoutDriver') },
    { value: AdminOrderFilter.WAITING_PICKUP, label: t('orders.filters.waitingPickup') },
    { value: AdminOrderFilter.IN_DELIVERY, label: t('orders.filters.inDelivery') },
    { value: AdminOrderFilter.GROUP_FINISHED_ORDERS, label: t('orders.filters.completedAll') },
    { value: AdminOrderFilter.COMPLETED, label: t('orders.filters.completed') },
    { value: AdminOrderFilter.CANCELLED, label: t('orders.filters.cancelled') },
    { value: AdminOrderFilter.ALL, label: t('orders.filters.all') },
  ];

  return (
    <div className="page">
      <div className="page-header">
        <h1>{t('orders.title')}</h1>
        <p>{t('orders.subtitle')}</p>
      </div>
      <div className="page-content">
        {/* {successMessage && (
          <div style={{
            padding: '1rem',
            backgroundColor: '#d4edda',
            color: '#155724',
            border: '1px solid #c3e6cb',
            borderRadius: '4px',
            marginBottom: '1.5rem'
          }}>
            {successMessage}
          </div>
        )} */}

        {/* <div style={{ marginBottom: '1.5rem' }}>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="search-button"
            style={{ padding: '0.75rem 1.5rem' }}
          >
            + {t('orders.createOrder')}
          </button>
        </div> */}

        <div className="search-section">
          <form onSubmit={handleSearch} className="search-form">
            <div className="search-controls">
              <select
                value={filter}
                onChange={(e) => updateFilter(e.target.value as AdminOrderFilter)}
                className="search-select"
              >
                {filterOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => updateSearchQuery(e.target.value)}
                placeholder={t('orders.searchOrders')}
                className="search-input"
              />
              <button type="submit" className="search-button" disabled={loading}>
                {loading ? t('common.loading') : t('common.search')}
              </button>
            </div>
          </form>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1rem',
          padding: '0.75rem 1rem',
          backgroundColor: '#f8f9fa',
          borderRadius: '4px',
          border: '1px solid #dee2e6'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '0.9rem', color: '#6c757d' }}>
              {t('orders.autoRefreshIn')}
            </span>
            <span style={{
              fontWeight: 'bold',
              fontSize: '1rem',
              color: secondsUntilRefresh <= 10 ? '#dc3545' : '#007bff',
              minWidth: '50px',
              textAlign: 'center'
            }}>
              {secondsUntilRefresh}s
            </span>
          </div>
          <button
            onClick={handleManualRefresh}
            disabled={loading}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: loading ? '#6c757d' : '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '0.9rem',
              fontWeight: '500',
              transition: 'all 0.2s',
              opacity: loading ? 0.6 : 1
            }}
            onMouseEnter={(e) => {
              if (!loading) {
                e.currentTarget.style.backgroundColor = '#218838';
              }
            }}
            onMouseLeave={(e) => {
              if (!loading) {
                e.currentTarget.style.backgroundColor = '#28a745';
              }
            }}
          >
            {loading ? t('orders.refreshing') : `🔄 ${t('orders.refreshNow')}`}
          </button>
        </div>

        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>{t('orders.orderId')}</th>
                <th>{t('orders.customer')}</th>
                <th>{t('orders.restaurant')}</th>
                <th>{t('orders.driver')}</th>
                <th>{t('common.status')}</th>
                <th>{t('orders.total')}</th>
                <th>{t('common.date')}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="no-data">{t('orders.loadingOrders')}</td>
                </tr>
              ) : orders.length === 0 ? (
                <tr>
                  <td colSpan={7} className="no-data">{t('orders.noOrders')}</td>
                </tr>
              ) : (
                orders.map((order) => (
                  <tr
                    key={order.id}
                    className="clickable-row"
                    onClick={() => handleOrderClick(order.id)}
                  >
                    <td>#{order.id}</td>
                    <td>
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(`/clients/${order.client!.id}`, '_blank');
                        }}
                        style={{
                          cursor: 'pointer',
                          color: '#007bff',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          display: 'inline-block',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#e7f3ff';
                          e.currentTarget.style.textDecoration = 'underline';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.textDecoration = 'none';
                        }}
                        title={t('common.viewDetails')}
                      >
                        <div>{order.client.name || 'N/A'}</div>
                        <div className="sub-text">{order.client.phone}</div>
                      </div>
                    </td>
                    <td>
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(`/restaurants/${order.restaurant!.id}`, '_blank');
                        }}
                        style={{
                          cursor: 'pointer',
                          color: '#007bff',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          display: 'inline-block',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#e7f3ff';
                          e.currentTarget.style.textDecoration = 'underline';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.textDecoration = 'none';
                        }}
                        title={t('common.viewDetails')}
                      >
                        <div>{order.restaurant.name}</div>
                        <div className="sub-text">{order.restaurant.phone}</div>
                      </div>
                    </td>
                    <td>
                      {order.assignedDriver ? (
                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(`/drivers/${order.assignedDriver!.id}`, '_blank');
                          }}
                          style={{
                            cursor: 'pointer',
                            color: '#007bff',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            display: 'inline-block',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#e7f3ff';
                            e.currentTarget.style.textDecoration = 'underline';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.textDecoration = 'none';
                          }}
                          title={t('common.viewDetails')}
                        >
                          <div>{order.assignedDriver.fullName}</div>
                          <div className="sub-text">{order.assignedDriver.phone}</div>
                        </div>
                      ) : (
                        <span className="no-driver">{t('orders.noDriverAssigned')}</span>
                      )}
                    </td>
                    <td>
                      <div>
                        <span
                          className="status-badge"
                          style={{ backgroundColor: getStatusColor(order) }}
                        >
                          {getStatusDisplay(order)}
                        </span>
                        {(() => {
                          if (order.orderStatus == OrderStatus.ORDER_ACCEPTED_BY_ADMIN && order.restaurantRejectionReason) {
                            return (
                              <div className="sub-text" style={{ marginTop: '4px', fontSize: '0.85rem' }}>
                                {order.restaurantRejectionReason}
                              </div>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    </td>
                    <td>{formatPrice(order.totalPrice)}</td>
                    <td>{formatDate(order.createdAt)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {orders.length > 0 && (
          <div className="pagination">
            <button
              onClick={() => updatePage(Math.max(currentPage - 1, 1))}
              disabled={currentPage === 1 || loading}
              className="pagination-button"
            >
              {t('common.previous')}
            </button>
            <span className="pagination-info">
              {t('orders.page')} {currentPage} - {orders.length} {t('orders.orders')}
            </span>
            <button
              onClick={() => updatePage(currentPage + 1)}
              disabled={orders.length < pageSize || loading}
              className="pagination-button"
            >
              {t('common.next')}
            </button>
          </div>
        )}
      </div>

      {/* <CreateOrderModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateOrder}
      /> */}
    </div>
  );
};

export default Orders;